import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CancelBookingInput, DeviceDto, StopSessionInput } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { timeOfDay } from '@/features/bookings/bookingLogic';

/**
 * Ending what a device is committed to — from the tile's "Stop & close", or
 * from the Actions menu's "End reservation". One operation, two doors:
 *
 * - a RUNNING session -> `POST /sessions/:id/stop`. The clock stops, the time
 *   played lands on the tab as money owed, and the PC returns to its sign-in
 *   screen on its own within a poll. Nothing is collected here: paying happens
 *   at the till, on the Outstanding screen.
 * - otherwise an upcoming BOOKING -> `POST /bookings/:id/cancel`, with the
 *   same optional reason the Bookings board's cancel records.
 *
 * **Rounds are asked for, not assumed.** A ROUND-billed device — a pool table —
 * is priced purely off how many games were played (`computeSessionAmount`
 * ignores elapsed time for these), so stopping one with the schema's default of
 * zero rounds bills the customer NOTHING. This dialog used to post an empty
 * body for every device, which meant every table ended free. Hourly machines
 * keep the plain confirm; there is nothing to count.
 */
export function EndReservationDialog({
  device,
  onClose,
  onDone,
}: {
  device: DeviceDto;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const session = device.currentSession;
  const booking = device.nextBooking;
  const countsRounds = Boolean(session) && device.reservationType === 'ROUND';
  // A 3-ball round is only a choice where the device has a 3-ball price: the
  // server bills `(threeBallPriceMd ?? 0) × rounds`, so offering it on a table
  // without one ended those games free. The till's own round picker has the
  // same rule (TabCheckoutDrawer).
  const offersThreeBall = device.threeBallPriceMd != null;

  const [reason, setReason] = useState('');
  const [pickedRoundType, setRoundType] = useState<'SINGLE' | 'DOUBLE' | 'THREE_BALL'>('SINGLE');
  // A 3-ball pick on a device that has since lost its 3-ball price (the board
  // polls under an open dialog) falls back to what the select now shows.
  const roundType =
    pickedRoundType === 'THREE_BALL' && !offersThreeBall ? 'SINGLE' : pickedRoundType;
  const [rounds, setRounds] = useState('1');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const roundCount = Math.max(0, Math.floor(Number(rounds) || 0));

  async function handleConfirm(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      if (session) {
        const payload: StopSessionInput = countsRounds
          ? {
              singleRounds: roundType === 'SINGLE' ? roundCount : 0,
              doubleRounds: roundType === 'DOUBLE' ? roundCount : 0,
              threeBallRounds: roundType === 'THREE_BALL' ? roundCount : 0,
            }
          : {};
        await api.post(`/sessions/${session.id}/stop`, payload);
      } else if (booking) {
        const payload: CancelBookingInput = { reason: reason.trim() || null };
        await api.post(`/bookings/${booking.id}/cancel`, payload);
      }
      onDone();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('sessions.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  const title = session
    ? t('deviceBoard.endDialog.stopTitle', { device: device.name })
    : t('deviceBoard.endDialog.cancelTitle', { device: device.name });

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary';

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-sm rounded-card border border-subtle bg-raised p-5 shadow-2xl">
        <h2 className="text-lg font-semibold text-content">{title}</h2>

        {session ? (
          <>
            <p className="mt-2 text-sm text-content-secondary">
              {t('deviceBoard.endDialog.stopMessage', { tab: session.tabName })}
            </p>

            {countsRounds ? (
              <div className="mt-4 grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs text-content-secondary">
                    {t('sessions.roundType')}
                  </span>
                  <select
                    value={roundType}
                    onChange={(event) => setRoundType(event.target.value as typeof roundType)}
                    className={fieldClass}
                  >
                    <option value="SINGLE">{t('sessions.single')}</option>
                    <option value="DOUBLE">{t('sessions.double')}</option>
                    {offersThreeBall ? (
                      <option value="THREE_BALL">{t('sessions.threeBall')}</option>
                    ) : null}
                  </select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs text-content-secondary">
                    {t('sessions.roundsPlayed')}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={rounds}
                    onChange={(event) => setRounds(event.target.value)}
                    autoFocus
                    className={`${fieldClass} tabular`}
                  />
                </label>
              </div>
            ) : null}

            {countsRounds && roundCount === 0 ? (
              // Legal — a table someone sat at and never played on — but it
              // charges nothing, so it must be a choice rather than a default
              // nobody noticed.
              <p className="mt-3 text-xs text-ending">{t('deviceBoard.endDialog.noRounds')}</p>
            ) : null}
          </>
        ) : booking ? (
          <>
            <p className="mt-2 text-sm text-content-secondary">
              {t('deviceBoard.endDialog.cancelMessage', {
                name: booking.partyName,
                time: timeOfDay(booking.startsAt),
              })}
            </p>
            <label className="mt-3 block">
              <span className="mb-1 block text-xs text-content-secondary">
                {t('bookings.cancelDialog.reason')}
              </span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={200}
                rows={2}
                className={fieldClass}
              />
            </label>
          </>
        ) : (
          // Menu raced the poll: whatever was here is already gone.
          <p className="mt-2 text-sm text-content-muted">{t('deviceBoard.endDialog.nothing')}</p>
        )}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          {session || booking ? (
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={submitting}
              className="rounded-full bg-danger px-4 py-2 text-sm font-medium text-content-inverse hover:bg-danger-hover disabled:opacity-60"
            >
              {submitting
                ? t('common.loading')
                : session
                  ? t('deviceBoard.endDialog.stopConfirm')
                  : t('deviceBoard.endDialog.cancelConfirm')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
